/**
 * Centralized API Endpoints Configuration
 * All API routes are defined here as constants for easy maintenance
 */

import { API_BASE_URL } from 'constants';

// Check if base URL already includes 'api/v1' or ends with '/api' - if yes, don't add prefix; otherwise add it
const baseUrl = API_BASE_URL;
const baseUrlHasApiVersion = baseUrl.includes('api/v1') || baseUrl.includes('api/v2');
const baseUrlEndsWithApi = baseUrl.endsWith('/api') || baseUrl.endsWith('/api/');

// If URL has api/v1 or api/v2, don't add any prefix
// If URL ends with /api, add only 'v1' (not 'api/v1')
// Otherwise, add full 'api/v1' prefix
let API_PREFIX = '';
if (baseUrlHasApiVersion) {
  API_PREFIX = ''; // Already has version, no prefix needed
} else if (baseUrlEndsWithApi) {
  API_PREFIX = 'v1/'; // URL ends with /api, just add v1
} else {
  API_PREFIX = 'api/v1/'; // No api in URL, add full prefix
}

const ADMIN_PREFIX = `${API_PREFIX}admin`;

/**
 * API Endpoints organized by module
 */
export const API_ENDPOINTS = {
  // ============================================
  // AUTH ENDPOINTS
  // ============================================
  AUTH: {
    LOGIN: `${API_PREFIX}auth/login`,
    LOGOUT: `${API_PREFIX}auth/logout`,
    REGISTER: `${API_PREFIX}auth/register`,
    FORGOT_PASSWORD: `${API_PREFIX}auth/forgot-password`,
    RESET_PASSWORD: `${API_PREFIX}auth/reset-password`,
    REFRESH_TOKEN: `${API_PREFIX}auth/refresh-token`,
    VERIFY_EMAIL: `${API_PREFIX}auth/verify-email`,
    ME: `${API_PREFIX}auth/me`,
    PROFILE: `${API_PREFIX}auth/admin/profile`,
  },

  // ============================================
  // USER MANAGEMENT
  // ============================================
  ROLES: {
    BASE: `${ADMIN_PREFIX}/roles`,
    ALL: `${ADMIN_PREFIX}/roles`,
    BY_ID: (id) => `${ADMIN_PREFIX}/roles/${id}`,
    CREATE: `${ADMIN_PREFIX}/roles`,
    UPDATE: (id) => `${ADMIN_PREFIX}/roles/${id}`,
    DELETE: (id) => `${ADMIN_PREFIX}/roles/${id}`,
    ASSIGN: `${ADMIN_PREFIX}/roles/assign`,
    MODULES: `${ADMIN_PREFIX}/roles/modules`,
  },

  USERS: {
    BASE: `${ADMIN_PREFIX}/users`,
    ALL: `${ADMIN_PREFIX}/users`,
    BY_ID: (id) => `${ADMIN_PREFIX}/users/${id}`,
    CREATE: `${ADMIN_PREFIX}/users`,
    UPDATE: (id) => `${ADMIN_PREFIX}/users/${id}`,
    DELETE: (id) => `${ADMIN_PREFIX}/users/${id}`,
    TOGGLE_STATUS: (id) => `${ADMIN_PREFIX}/users/${id}/status`,
    PROFILE: `${ADMIN_PREFIX}/users/profile`,
    UPDATE_PROFILE: `${ADMIN_PREFIX}/users/profile`,
    CHANGE_PASSWORD: `${ADMIN_PREFIX}/users/change-password`,
  },

  // ============================================
  // VACCINE MANAGEMENT
  // ============================================
  VACCINES: {
    BASE: `${API_PREFIX}vaccines`,
    ALL: `${ADMIN_PREFIX}/vaccines`,
    BY_ID: (id) => `${API_PREFIX}vaccines/${id}`,
    CREATE: `${API_PREFIX}vaccines`,
    UPDATE: (id) => `${API_PREFIX}vaccines/${id}`,
    DELETE: (id) => `${API_PREFIX}vaccines/${id}`,
    TOGGLE_STATUS: (id) => `${API_PREFIX}vaccines/${id}/status`,
    AGE_GROUPS: `${API_PREFIX}vaccines/age-groups`,
    BY_AGE_GROUP: (ageGroup) => `${API_PREFIX}vaccines?ageGroup=${ageGroup}`,
  },

  // ============================================
  // VACCINE INVENTORY / STOCK
  // ============================================
  STOCK: {
    BASE: `${ADMIN_PREFIX}/vaccine-inventory`,
    ALL: `${ADMIN_PREFIX}/vaccine-inventory`,
    BY_ID: (id) => `${ADMIN_PREFIX}/vaccine-inventory/${id}`,
    CREATE: `${ADMIN_PREFIX}/vaccine-inventory`,
    UPDATE: (id) => `${ADMIN_PREFIX}/vaccine-inventory/${id}`,
    DELETE: (id) => `${ADMIN_PREFIX}/vaccine-inventory/${id}`,
    LOW_STOCK: `${ADMIN_PREFIX}/vaccine-inventory/low-stock`,
    EXPIRED: `${ADMIN_PREFIX}/vaccine-inventory/expired`,
     AGE_GROUPS: `${API_PREFIX}vaccines/age-groups`,
    BY_AGE_GROUP: (ageGroup) => `${API_PREFIX}vaccines?ageGroup=${ageGroup}`,
    BY_CENTER: (centerId) => `${ADMIN_PREFIX}/vaccine-inventory?centerId=${centerId}`,
    BY_VACCINE: (vaccineId) => `${ADMIN_PREFIX}/vaccine-inventory?vaccineId=${vaccineId}`,
    ADJUST: (id) => `${ADMIN_PREFIX}/vaccine-inventory/${id}/adjust`,
  },

   // ============================================
  // VACCINE INVENTORY / STOCK
  // ============================================
  PLAN: {
    BASE: `${API_PREFIX}vaccine-schedules`,
    ALL: `${API_PREFIX}vaccine-schedules`,
    BY_ID: (id) => `${API_PREFIX}vaccine-schedules/${id}`,
    CREATE: `${API_PREFIX}vaccine-schedules`,
    UPDATE: (id) => `${API_PREFIX}vaccine-schedules/${id}`,
    DELETE: (id) => `${API_PREFIX}vaccine-schedules/${id}`,
    LOW_STOCK: `${API_PREFIX}vaccine-schedules/low-stock`,
    EXPIRED: `${API_PREFIX}vaccine-schedules/expired`,
    BY_CENTER: (centerId) => `${API_PREFIX}vaccine-schedules?centerId=${centerId}`,
    BY_VACCINE: (vaccineId) => `${API_PREFIX}vaccine-schedules?vaccineId=${vaccineId}`,
    ADJUST: (id) => `${API_PREFIX}vaccine-schedules/${id}/adjust`,
  },

  // ============================================
  // VACCINE DISPATCH / TRANSFERS
  // ============================================
  VACCINE_DISPATCH: {
    BASE: `${API_PREFIX}vaccine-transfers`,
    ALL: `${ADMIN_PREFIX}/vaccine-transfers`,
    BY_ID: (id) => `${API_PREFIX}vaccine-transfers/${id}`,
    CREATE: `${API_PREFIX}vaccine-transfers`,
    UPDATE: (id) => `${API_PREFIX}vaccine-transfers/${id}`,
    DELETE: (id) => `${API_PREFIX}vaccine-transfers/${id}`,
    UPDATE_STATUS: (id) => `${API_PREFIX}vaccine-transfers/${id}/status`,
    RECEIVE: (id) => `${API_PREFIX}vaccine-transfers/receive/${id}`,
    INSPECT: (id) => `${API_PREFIX}vaccine-transfers/inspect/${id}`,
    STATUSES: `${API_PREFIX}dispatch-status`,
    COURIERS: `${API_PREFIX}couriers`,
    CONDITIONS: `${API_PREFIX}dispatch-conditions`,
    BY_STATUS: (status) => `${API_PREFIX}vaccine-transfers?status=${status}`,
    BY_DATE_RANGE: (from, to) => `${API_PREFIX}vaccine-transfers?fromDate=${from}&toDate=${to}`,
  },

  // ============================================
  // VACCINE RECEIVE
  // ============================================
  VACCINE_RECEIVE: {
    BASE: `${API_PREFIX}vaccine-receive`,
    ALL: `${API_PREFIX}vaccine-receive`,
    BY_ID: (id) => `${API_PREFIX}vaccine-receive/${id}`,
    CREATE: `${API_PREFIX}vaccine-receive`,
    UPDATE: (id) => `${API_PREFIX}vaccine-receive/${id}`,
    DELETE: (id) => `${API_PREFIX}vaccine-receive/${id}`,
    CONFIRM: (id) => `${API_PREFIX}vaccine-receive/${id}/confirm`,
  },

  // ============================================
  // VACCINE RECORD
  // ============================================
  // VACCINATION_RECORD: {
  //   BASE: `vaccine-records`,
  //   ALL: `vaccine-records`,
  //   BY_ID: (id) => `vaccine-records/${id}`,
  //   CREATE: `vaccine-records`,
  //   UPDATE: (id) => `vaccine-records/${id}`,
  //   DELETE: (id) => `vaccine-records/${id}`,
  //   BY_CHILD: (childId) => `vaccine-records?childId=${childId}`,
  //   BY_VACCINE: (vaccineId) => `vaccine-records?vaccineId=${vaccineId}`,
  // },
VACCINATION_RECORD: {
  BASE: `${ADMIN_PREFIX}/vaccination-records`,

  ALL: (page = 1, limit = 10) =>
    `${ADMIN_PREFIX}/vaccination-records?page=${page}&limit=${limit}`,

  BY_ID: (id) =>
    `${ADMIN_PREFIX}/vaccination-records/${id}`,

  BY_CHILD: (childId, page = 1, limit = 10) =>
    `${ADMIN_PREFIX}/vaccination-records?childId=${childId}&page=${page}&limit=${limit}`,
},

  // ============================================
  // SUPPLIERS
  // ============================================
  SUPPLIERS: {
    BASE: `${ADMIN_PREFIX}/suppliers`,
    ALL: `${ADMIN_PREFIX}/suppliers`,
    BY_ID: (id) => `${ADMIN_PREFIX}/suppliers/${id}`,
    CREATE: `${ADMIN_PREFIX}/suppliers`,
    UPDATE: (id) => `${ADMIN_PREFIX}/suppliers/${id}`,
    DELETE: (id) => `${ADMIN_PREFIX}/suppliers/${id}`,
    TOGGLE_STATUS: (id) => `${ADMIN_PREFIX}/suppliers/${id}/status`,
    BY_STATUS: (status) => `${ADMIN_PREFIX}/suppliers?status=${status}`,
  },

  // ============================================
  // PATIENTS / PARENTS
  // ============================================
  PATIENTS: {
    BASE: `${API_PREFIX}patients`,
    ALL: `${API_PREFIX}patients`,
    BY_ID: (id) => `${API_PREFIX}patients/${id}`,
    CREATE: `${API_PREFIX}patients`,
    UPDATE: (id) => `${API_PREFIX}patients/${id}`,
    DELETE: (id) => `${API_PREFIX}patients/${id}`,
    SEARCH: (query) => `${API_PREFIX}patients?search=${query}`,
  },

  //=================================
  // child
  //=================================

  // CHILD: {
  //   BASE: `${ADMIN_PREFIX}/children`,
  //   ALL: `${ADMIN_PREFIX}/children`,
  //   BY_ID: (id) => `${ADMIN_PREFIX}/children/${id}`,
  //   CREATE: `${ADMIN_PREFIX}/children`,
  //   UPDATE: (id) => `${ADMIN_PREFIX}/children/${id}`,
  //   DELETE: (id) => `${ADMIN_PREFIX}/children/${id}`,
  //   SEARCH: (query) => `${ADMIN_PREFIX}/children?search=${query}`,
  // },

  PARENTS: {
    BASE: `${ADMIN_PREFIX}/parents`,
    ALL: `${ADMIN_PREFIX}/parents`,
    BY_ID: (id) => `${ADMIN_PREFIX}/parents/${id}`,
    CREATE: `${ADMIN_PREFIX}/parents`,
    UPDATE: (id) => `${ADMIN_PREFIX}/parents/${id}`,
    DELETE: (id) => `${ADMIN_PREFIX}/parents/${id}`,
    CHILDREN: (id) => `${ADMIN_PREFIX}/parents/children/${id}`,
  },

  // ============================================
  // CHILDREN
  // ============================================
  // CHILDREN: {
  //   BASE: `${ADMIN_PREFIX}/children`,
  //   ALL: `${ADMIN_PREFIX}/children`,
  //   BY_ID: (id) => `${ADMIN_PREFIX}/children/${id}`,
  //   CREATE: `${ADMIN_PREFIX}/children`,
  //   UPDATE: (id) => `${ADMIN_PREFIX}/children/${id}`,
  //   DELETE: (id) => `${ADMIN_PREFIX}/children/${id}`,
  //   BY_PARENT: (parentId) => `${ADMIN_PREFIX}/children?parentId=${parentId}`,
  //   VACCINATION_HISTORY: (id) => `${ADMIN_PREFIX}/children/${id}/vaccinations`,
  // },

  CHILDREN: {
  BASE: `${ADMIN_PREFIX}/children`,
  LIST: `${ADMIN_PREFIX}/children`,
  BY_ID: (id) => `${ADMIN_PREFIX}/children/${id}`,
  CREATE: `${ADMIN_PREFIX}/children`,
  UPDATE: (id) => `${ADMIN_PREFIX}/children/${id}`,
  DELETE: (id) => `${ADMIN_PREFIX}/children/${id}`,
  BY_PARENT: (parentId) =>
    `${ADMIN_PREFIX}/children?parentId=${parentId}`,
  VACCINATION_HISTORY: (id) =>
    `${ADMIN_PREFIX}/children/${id}/vaccinations`,
  IMPORT_UPLOAD: `${ADMIN_PREFIX}/children/import/upload`,
  IMPORT_CONFIRM: `${ADMIN_PREFIX}/children/import/confirm`,
  IMPORT_SAMPLE: `${ADMIN_PREFIX}/children/import/sample`,
  VACCINATION_PLAN: (childId) => `${ADMIN_PREFIX}/child-vaccination-plans/${childId}`,
  VACCINATION_PLAN_ITEM: (childId, planId) => `${ADMIN_PREFIX}/child-vaccination-plans/${childId}/${planId}`,
  VACCINATION_PLANS_LIST: `${ADMIN_PREFIX}/child-vaccination-plans`,
},

  // ============================================
  // APPOINTMENTS
  // ============================================
  APPOINTMENTS: {
    BASE: `${API_PREFIX}appointments`,
    ALL: `${ADMIN_PREFIX}/appointments`,
    BY_ID: (id) => `${API_PREFIX}appointments/${id}`,
    CREATE: `${API_PREFIX}appointments`,
    UPDATE: (id) => `${API_PREFIX}appointments/${id}`,
    DELETE: (id) => `${API_PREFIX}appointments/${id}`,
    CANCEL: (id) => `${API_PREFIX}appointments/${id}/cancel`,
    COMPLETE: (id) => `${API_PREFIX}appointments/${id}/complete`,
    RESCHEDULE: (id) => `${API_PREFIX}appointments/${id}/reschedule`,
    BY_DATE: (date) => `${API_PREFIX}appointments?date=${date}`,
    BY_STATUS: (status) => `${API_PREFIX}appointments?status=${status}`,
    BY_CENTER: (centerId) => `${API_PREFIX}appointments?centerId=${centerId}`,
    UPCOMING: `${API_PREFIX}appointments/upcoming`,
    TODAY: `${API_PREFIX}appointments/today`,
  },

  // ============================================
  // VACCINATION CENTERS
  // ============================================
  CENTERS: {
    BASE: `${ADMIN_PREFIX}/vaccination-centers`,
    ALL: `${ADMIN_PREFIX}/vaccination-centers`,
    BY_ID: (id) => `${ADMIN_PREFIX}/vaccination-centers/${id}`,
    CREATE: `${ADMIN_PREFIX}/vaccination-centers`,
    UPDATE: (id) => `${ADMIN_PREFIX}/vaccination-centers/${id}`,
    DELETE: (id) => `${ADMIN_PREFIX}/vaccination-centers/${id}`,
    TOGGLE_STATUS: (id) => `${ADMIN_PREFIX}/vaccination-centers/${id}/status`,
    STATUSES: `${ADMIN_PREFIX}/center-status`,
    ROLES: `${ADMIN_PREFIX}/center-roles`,
    STAFF: (id) => `${ADMIN_PREFIX}/vaccination-centers/${id}/staff`,
    INVENTORY: (id) => `${ADMIN_PREFIX}/vaccination-centers/${id}/inventory`,
    BY_DISTRICT: (districtId) => `${ADMIN_PREFIX}/vaccination-centers?districtId=${districtId}`,
  },

  // ============================================
  // MEDICAL STAFF
  // ============================================
  MEDICAL_STAFF: {
    BASE: `${ADMIN_PREFIX}/medical-staff`,
    ALL: `${ADMIN_PREFIX}/medical-staff`,
    BY_ID: (id) => `${ADMIN_PREFIX}/medical-staff/${id}`,
    CREATE: `${ADMIN_PREFIX}/medical-staff`,
    UPDATE: (id) => `${ADMIN_PREFIX}/medical-staff/${id}`,
    DELETE: (id) => `${ADMIN_PREFIX}/medical-staff/${id}`,
    TOGGLE_STATUS: (id) => `${ADMIN_PREFIX}/medical-staff/${id}/status`,
    BY_CENTER: (centerId) => `${ADMIN_PREFIX}/medical-staff?centerId=${centerId}`,
    BY_ROLE: (role) => `${ADMIN_PREFIX}/medical-staff?role=${role}`,
  },

  // ============================================
  // LOCATION: DISTRICTS
  // ============================================
  DISTRICTS: {
    BASE: `${API_PREFIX}districts`,
    ALL: `${ADMIN_PREFIX}/districts`,
    BY_ID: (id) => `${API_PREFIX}districts/${id}`,
    CREATE: `${ADMIN_PREFIX}/districts`,
    UPDATE: (id) => `${ADMIN_PREFIX}/districts/${id}`,
    DELETE: (id) => `${ADMIN_PREFIX}/districts/${id}`,
    ADMIN: `${ADMIN_PREFIX}/districts`,
    TALUKAS: (id) => `${API_PREFIX}districts/${id}/talukas`,
  },

  // ============================================
  // LOCATION: TALUKAS
  // ============================================
  TALUKAS: {
    BASE: `${API_PREFIX}talukas`,
    ALL: `${ADMIN_PREFIX}/talukas`,
    BY_ID: (id) => `${API_PREFIX}talukas/${id}`,
    CREATE: `${ADMIN_PREFIX}/talukas`,
    UPDATE: (id) => `${ADMIN_PREFIX}/talukas/${id}`,
    DELETE: (id) => `${ADMIN_PREFIX}/talukas/${id}`,
    BY_DISTRICT: (districtId) => `${API_PREFIX}talukas?districtId=${districtId}`,
    ADMIN: `${ADMIN_PREFIX}/talukas`,
    VILLAGES: (id) => `${API_PREFIX}talukas/${id}/villages`,
  },

  // ============================================
  // LOCATION: VILLAGES
  // ============================================
  VILLAGES: {
    BASE: `${API_PREFIX}villages`,
    ALL: `${ADMIN_PREFIX}/villages`,
    BY_ID: (id) => `${API_PREFIX}villages/${id}`,
    CREATE: `${ADMIN_PREFIX}/villages`,
    UPDATE: (id) => `${ADMIN_PREFIX}/villages/${id}`,
    DELETE: (id) => `${ADMIN_PREFIX}/villages/${id}`,
    BY_TALUKA: (talukaId) => `${API_PREFIX}villages?talukaId=${talukaId}`,
    BY_DISTRICT: (districtId) => `${API_PREFIX}villages?districtId=${districtId}`,
    ADMIN: `${ADMIN_PREFIX}/villages`,
  },

  // ============================================
  // DROPDOWNS
  // ============================================
  DROPDOWNS: {
    BASE: `${API_PREFIX}dropdowns`,
    VACCINATION_CENTERS: `${API_PREFIX}dropdowns/vaccination-centers`,
    MEDICAL_STAFF: `${API_PREFIX}dropdowns/medical-staff`,
    VILLAGES_WITH_LOCATION: `${API_PREFIX}dropdowns/villages-with-location`,
    SUPPLIER_STATUS: `${API_PREFIX}dropdowns/supplier-status`,
    SUPPLIER_RATINGS: `${API_PREFIX}dropdowns/supplier-ratings`,
    ACCOUNT_TYPES: `${API_PREFIX}dropdowns/account-types`,
    DISTRICTS: `${API_PREFIX}dropdowns/districts`,
    TALUKAS: `${API_PREFIX}dropdowns/talukas`,
    VILLAGES: `${API_PREFIX}dropdowns/villages`,
    VACCINES: `${API_PREFIX}dropdowns/vaccines`,
    AGE_GROUPS: `${API_PREFIX}dropdowns/age-groups`,
  },

  // ============================================
  // FILE UPLOAD
  // ============================================
  UPLOAD: {
    BASE: `${API_PREFIX}upload`,
    PROFILE_PHOTO: `${API_PREFIX}upload/profile-photo`,
    DOCUMENT: `${API_PREFIX}upload/document`,
    IMAGE: `${API_PREFIX}upload/image`,
    DELETE: (id) => `${API_PREFIX}upload/${id}`,
  },

  // ============================================
  // DASHBOARD / ANALYTICS
  // ============================================
  DASHBOARD: {
    BASE: `${API_PREFIX}dashboard`,
    STATS: `${API_PREFIX}dashboard/stats`,
    RECENT_APPOINTMENTS: `${API_PREFIX}dashboard/recent-appointments`,
    VACCINE_STOCK: `${API_PREFIX}dashboard/vaccine-stock`,
    UPCOMING_APPOINTMENTS: `${API_PREFIX}dashboard/upcoming-appointments`,
    VACCINATION_TRENDS: `${API_PREFIX}dashboard/vaccination-trends`,
    CENTER_PERFORMANCE: `${API_PREFIX}dashboard/center-performance`,
  },

  // ============================================
  // REPORTS (Vaccine Inventory Report System)
  // ============================================
  REPORTS: {
    BASE:       `${ADMIN_PREFIX}/reports`,
    GENERATE:   `${ADMIN_PREFIX}/reports/generate`,
    DETAIL:     (id) => `${ADMIN_PREFIX}/reports/${id}`,
    ROWS:       (id) => `${ADMIN_PREFIX}/reports/${id}/rows`,
    DOWNLOAD:   (id) => `${ADMIN_PREFIX}/reports/${id}/download`,
    DELETE:     (id) => `${ADMIN_PREFIX}/reports/${id}`,
    REGENERATE: (id) => `${ADMIN_PREFIX}/reports/${id}/regenerate`,
  },

  // ============================================
  // NOTIFICATIONS
  // ============================================
  NOTIFICATIONS: {
    BASE: `${API_PREFIX}notifications`,
    ALL: `${API_PREFIX}notifications`,
    BY_ID: (id) => `${API_PREFIX}notifications/${id}`,
    DELETE: (id) => `${API_PREFIX}notifications/${id}`,
    MARK_READ: (id) => `${API_PREFIX}notifications/${id}/read`,
    MARK_ALL_READ: `${API_PREFIX}notifications/read-all`,
    UNREAD_COUNT: `${API_PREFIX}notifications/unread-count`,
    SETTINGS: `${API_PREFIX}notifications/settings`,
  },

  // ============================================
  // GENERAL SETTINGS
  // ============================================
  SETTINGS: {
    GENERAL: `${ADMIN_PREFIX}/settings`,
    TEST_LOW_STOCK_EMAIL: `${ADMIN_PREFIX}/settings/test-email`,
    CENTER_NOTIFICATION_EMAILS: `${ADMIN_PREFIX}/settings/centers`,
    UPDATE_CENTER_NOTIFICATION_EMAILS: (centerId) => `${ADMIN_PREFIX}/settings/centers/${centerId}`,
    APP_SETTINGS: `${ADMIN_PREFIX}/settings/app`,
    APP_SETTING: (key) => `${ADMIN_PREFIX}/settings/app/${key}`,
  },

  // ============================================
  // ACTIVITY LOGS
  // ============================================
  LOGS: {
    ALL: `${ADMIN_PREFIX}/logs`,
    BY_ID: (id) => `${ADMIN_PREFIX}/logs/${id}`,
    DELETE: (id) => `${ADMIN_PREFIX}/logs/${id}`,
    MODULE_OPTIONS: `${ADMIN_PREFIX}/logs/module-options`,
  },

  // ============================================
  // EMAIL NOTIFICATION LOGS
  // ============================================
  NOTIFICATION_LOGS: {
    ALL: `${ADMIN_PREFIX}/notification-logs`,
    STATS: `${ADMIN_PREFIX}/notification-logs/stats`,
  },

  // ============================================
  // MODULES
  // ============================================
  MODULES: {
    ALL: `${ADMIN_PREFIX}/modules`,
    BY_ID: (id) => `${ADMIN_PREFIX}/modules/${id}`,
    CREATE: `${ADMIN_PREFIX}/modules`,
    UPDATE: (id) => `${ADMIN_PREFIX}/modules/${id}`,
    DELETE: (id) => `${ADMIN_PREFIX}/modules/${id}`,
  },

};

/**
 * Build URL with query parameters
 * @param {string} endpoint - Base endpoint
 * @param {Object} params - Query parameters
 * @returns {string} - URL with query string
 */
export const buildUrl = (endpoint, params = {}) => {
  const filteredParams = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

  if (Object.keys(filteredParams).length === 0) {
    return endpoint;
  }

  const queryString = new URLSearchParams(filteredParams).toString();
  const separator = endpoint.includes('?') ? '&' : '?';
  return `${endpoint}${separator}${queryString}`;
};

/**
 * Build paginated URL
 * @param {string} endpoint - Base endpoint
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @param {Object} additionalParams - Additional query parameters
 * @returns {string} - URL with pagination
 */
export const buildPaginatedUrl = (endpoint, page = 1, limit = 5, additionalParams = {}) => {
  return buildUrl(endpoint, { page, limit, ...additionalParams });
};

/**
 * Build search URL
 * @param {string} endpoint - Base endpoint
 * @param {string} search - Search query
 * @param {Object} additionalParams - Additional query parameters
 * @returns {string} - URL with search
 */
export const buildSearchUrl = (endpoint, search, additionalParams = {}) => {
  return buildUrl(endpoint, { search, ...additionalParams });
};

// Export individual endpoint groups for convenience
export const {
  AUTH,
  ROLES,
  USERS,
  VACCINES,
  STOCK,
  PLAN,
  VACCINE_DISPATCH,
  VACCINE_RECEIVE,
  VACCINATION_RECORD,
  SUPPLIERS,
  PATIENTS,
  PARENTS,
  CHILDREN,
  APPOINTMENTS,
  CENTERS,
  MEDICAL_STAFF,
  DISTRICTS,
  TALUKAS,
  VILLAGES,
  DROPDOWNS,
  UPLOAD,
  DASHBOARD,
  REPORTS,
  NOTIFICATIONS,
  SETTINGS,
  LOGS,
  NOTIFICATION_LOGS,
  MODULES,
} = API_ENDPOINTS;

export default API_ENDPOINTS;
